import { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import { OrganizationMemberApi, Configuration } from "account-common";
import { extractErrorMessage } from "../utils/errorUtils";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

export const useOrganizationMembers = (orgId) => {
  const auth = useAuth();
  const { t } = useTranslation("common");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set up the API client
  const api = useCallback(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken) return null;
    const config = new Configuration({
      basePath: ACCOUNT_API_BASE_URL,
      accessToken: accessToken,
    });
    return new OrganizationMemberApi(config);
  }, [auth.user?.access_token]);

  // Fetch members using the new listOrgMembers endpoint
  const fetchMembers = useCallback(async () => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await memberApi.listOrganizationMembers(orgId);
      
      if (response.data.status === "success" && response.data.data) {
        setMembers(response.data.data.members || []);
      } else {
        setError("Failed to load members");
      }
    } catch (err) {
      console.error("Failed to fetch members:", err);
      setError(extractErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [api, orgId, t]);

  // Add member to organization using email
  const addMember = useCallback(async (email) => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      throw new Error("No access token found or organization ID missing");
    }

    try {
      const response = await memberApi.createOrganizationMember(orgId, {
        email: email
      });
      
      if (response.data.status === "success" && response.data.data) {
        // Add the new member to the list
        const newMember = response.data.data.member;
        setMembers(prev => [...prev, newMember]);
        return response.data.data;
      } else {
        throw new Error("Failed to add member");
      }
    } catch (err) {
      console.error("Failed to add member:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, orgId, t]);

  // Remove member from organization
  const removeMember = useCallback(async (userId) => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      throw new Error("No access token found or organization ID missing");
    }

    try {
      const response = await memberApi.removeOrganizationMember(orgId, userId);
      
      if (response.data.status === "success") {
        // Remove the member from the list
        setMembers(prev => prev.filter(member => member.userId !== userId));
        return response.data;
      } else {
        throw new Error("Failed to remove member");
      }
    } catch (err) {
      console.error("Failed to remove member:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, orgId, t]);

  // Update member permissions (owner status)
  const updateMemberPermissions = useCallback(async (userId, isOwner) => {
    const memberApi = api();
    if (!memberApi || !orgId) {
      throw new Error("No access token found or organization ID missing");
    }

    try {
      const response = await memberApi.editOrganizationMember(orgId, userId, {
        owner: isOwner
      });
      
      if (response.data.status === "success") {
        // Update the member in the list
        setMembers(prev => 
          prev.map(member => 
            member.userId === userId 
              ? { ...member, owner: isOwner }
              : member
          )
        );
        return response.data;
      } else {
        throw new Error("Failed to update member permissions");
      }
    } catch (err) {
      console.error("Failed to update member permissions:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, orgId, t]);

  // Fetch members on mount and when orgId changes
  useEffect(() => {
    if (orgId) {
      fetchMembers();
    }
  }, [fetchMembers, orgId]);

  return {
    members,
    loading,
    error,
    fetchMembers,
    addMember,
    removeMember,
    updateMemberPermissions,
    refetch: fetchMembers
  };
}; 